import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import Preloader from './components/ui/Preloader';
import Seo from './components/Seo';
import SiteJsonLd from './components/SiteJsonLd';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Spotlight from './components/ui/Spotlight';
import ScrollTopBtn from './components/ui/ScrollTopBtn';
// Home reste en import statique : c'est la page d'entrée la plus fréquente,
// pas de round-trip réseau supplémentaire à payer pour son chunk.
import Home from './pages/Home';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteConfigProvider } from './context/SiteConfigContext';

// Toutes les autres pages sont chargées à la demande (code-splitting par
// route) — réduit le bundle JS initial, ce qui améliore les Core Web Vitals
// (facteur de classement Google), notamment pour les visiteurs qui n'ouvrent
// que l'accueil.
const About                  = lazy(() => import('./pages/About'));
const Programs                = lazy(() => import('./pages/Programs'));
const Contact                 = lazy(() => import('./pages/Contact'));
const News                    = lazy(() => import('./pages/News'));
const MentionsLegales         = lazy(() => import('./pages/legal/MentionsLegales'));
const PolitiqueConfidentialite = lazy(() => import('./pages/legal/PolitiqueConfidentialite'));
const PolitiqueCookies        = lazy(() => import('./pages/legal/PolitiqueCookies'));
const CGU                     = lazy(() => import('./pages/legal/CGU'));
const Annuaire                = lazy(() => import('./pages/annuaire/Annuaire'));
const CohorteHub              = lazy(() => import('./pages/cohortes/CohorteHub'));
const RegionPage              = lazy(() => import('./pages/cohortes/RegionPage'));
const CohortDetail            = lazy(() => import('./pages/cohortes/CohortDetail'));
const ParticipanteDetail      = lazy(() => import('./pages/cohortes/ParticipanteDetail'));
const ArticleDetail           = lazy(() => import('./pages/news/ArticleDetail'));
const EvenementDetail         = lazy(() => import('./pages/news/EvenementDetail'));
const Annonces                = lazy(() => import('./pages/news/Annonces'));
const Galerie                  = lazy(() => import('./pages/Galerie'));
const GalerieDetail            = lazy(() => import('./pages/GalerieDetail'));
const CandidaturePublique      = lazy(() => import('./pages/CandidaturePublique'));
const AppelCandidature         = lazy(() => import('./pages/AppelCandidature'));
const Desabonnement            = lazy(() => import('./pages/Desabonnement'));
const EnquetePublique          = lazy(() => import('./pages/EnquetePublique'));
const MonProfil                = lazy(() => import('./pages/profil/MonProfil'));

// Espace admin — jamais nécessaire pour un visiteur public, toujours différé.
const AdminLogin          = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout         = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard      = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminCohortes       = lazy(() => import('./pages/admin/AdminCohortes'));
const AdminParticipantes  = lazy(() => import('./pages/admin/AdminParticipantes'));
const AdminArticles       = lazy(() => import('./pages/admin/AdminArticles'));
const AdminEvenements     = lazy(() => import('./pages/admin/AdminEvenements'));
const AdminGalerie        = lazy(() => import('./pages/admin/AdminGalerie'));
const AdminParametres     = lazy(() => import('./pages/admin/AdminParametres'));
const AdminContacts       = lazy(() => import('./pages/admin/AdminContacts'));
const AdminNewsletter     = lazy(() => import('./pages/admin/AdminNewsletter'));
const AdminAnnonces       = lazy(() => import('./pages/admin/AdminAnnonces'));
const AdminCandidatures   = lazy(() => import('./pages/admin/AdminCandidatures'));
const AdminFormulaires    = lazy(() => import('./pages/admin/AdminFormulaires'));
const AdminTemoignages    = lazy(() => import('./pages/admin/AdminTemoignages'));
const AdminAnalytics      = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminTranslations   = lazy(() => import('./pages/admin/AdminTranslations'));
const AdminCommentaires   = lazy(() => import('./pages/admin/AdminCommentaires'));
const AdminUtilisateurs   = lazy(() => import('./pages/admin/AdminUtilisateurs'));
const AdminEnquetes       = lazy(() => import('./pages/admin/AdminEnquetes'));
const AdminEmails         = lazy(() => import('./pages/admin/AdminEmails'));

import './styles/global.css';
import './pages/Home.css';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

// Tracks public page views — fires once per navigation, skips /admin routes
function PageTracker() {
  const { pathname } = useLocation();
  const sessionIdRef = useRef(null);

  useEffect(() => {
    // Persist a sessionId in sessionStorage for unique visitor counting
    let sid = sessionStorage.getItem('_envol_sid');
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('_envol_sid', sid);
    }
    sessionIdRef.current = sid;
  }, []);

  useEffect(() => {
    if (pathname.startsWith('/admin')) return;

    // Analytics interne
    const sid = sessionIdRef.current || sessionStorage.getItem('_envol_sid');
    fetch('/api/analytics/track', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'page_view', page: pathname, sessionId: sid }),
    }).catch(() => {});

    // Google Analytics — page view SPA
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path:  pathname,
        page_title: document.title,
      });
    }
  }, [pathname]);

  return null;
}

function AppLayout() {
  return (
    <>
      <Spotlight />
      <Navbar />
      <ScrollToTop />
      <PageTracker />
      <main>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/programmes" element={<Programs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/actualites" element={<News />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/confidentialite" element={<PolitiqueConfidentialite />} />
          <Route path="/cookies" element={<PolitiqueCookies />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/annuaire" element={<Annuaire />} />
          <Route path="/cohortes" element={<CohorteHub />} />
          <Route path="/cohortes/:region" element={<RegionPage />} />
          <Route path="/cohortes/:region/:cohortId" element={<CohortDetail />} />
          <Route path="/cohortes/:region/:cohortId/participante/:pid" element={<ParticipanteDetail />} />
          <Route path="/actualites/article/:slug" element={<ArticleDetail />} />
          <Route path="/actualites/evenement/:id" element={<EvenementDetail />} />
          <Route path="/actualites/annonces" element={<Annonces />} />
          <Route path="/galerie" element={<Galerie />} />
          <Route path="/galerie/:id" element={<GalerieDetail />} />
          <Route path="/appel" element={<AppelCandidature />} />
          <Route path="/candidature" element={<CandidaturePublique />} />
          <Route path="/desabonnement" element={<Desabonnement />} />
          <Route path="/enquete/:token" element={<EnquetePublique />} />
          <Route path="/mon-profil/:token" element={<MonProfil />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      <Footer />
      <ScrollTopBtn />
    </>
  );
}

// Affiché brièvement pendant le chargement du chunk d'une route (code-splitting)
function RouteFallback() {
  return <div style={{ minHeight: '40vh' }} aria-hidden="true" />;
}

function AdminGuard() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <AdminLayout /> : <Navigate to="/admin/login" replace />;
}

function NotFound() {
  const { t } = useTranslation('common');
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 24, padding: '120px 48px 80px', textAlign: 'center' }}>
      <Seo title={t('errors.not_found_title')} noindex path={window.location.pathname} />
      <div style={{ fontFamily: 'var(--serif)', fontSize: 96, fontWeight: 300, color: 'var(--magenta)', lineHeight: 1 }}>404</div>
      <h1 style={{ fontFamily: 'var(--serif)', fontSize: 40, fontWeight: 300, color: 'var(--navy)' }}>{t('errors.not_found_title')}</h1>
      <p style={{ color: 'var(--muted)', fontSize: 18 }}>{t('errors.not_found_desc')}</p>
      <a href="/" className="btn-primary">{t('buttons.back_home')} <span className="arrow">→</span></a>
    </div>
  );
}

export default function App() {
  const [showPreloader, setShowPreloader] = useState(
    () => !sessionStorage.getItem('envol_visited')
  );

  return (
    <BrowserRouter>
      <SiteConfigProvider>
      <AuthProvider>
        <SiteJsonLd />
        {showPreloader && (
          <Preloader
            onComplete={() => {
              sessionStorage.setItem('envol_visited', '1');
              setShowPreloader(false);
            }}
          />
        )}
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminGuard />}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="cohortes" element={<AdminCohortes />} />
            <Route path="participantes" element={<AdminParticipantes />} />
            <Route path="articles" element={<AdminArticles />} />
            <Route path="evenements" element={<AdminEvenements />} />
            <Route path="galerie" element={<AdminGalerie />} />
            <Route path="newsletter"  element={<AdminNewsletter />} />
            <Route path="annonces" element={<AdminAnnonces />} />
            <Route path="candidatures" element={<AdminCandidatures />} />
            <Route path="formulaires" element={<AdminFormulaires />} />
            <Route path="temoignages" element={<AdminTemoignages />} />
            <Route path="contacts" element={<AdminContacts />} />
            <Route path="parametres" element={<AdminParametres />} />
            <Route path="analytics"     element={<AdminAnalytics />} />
            <Route path="traductions"    element={<AdminTranslations />} />
            <Route path="commentaires"  element={<AdminCommentaires />} />
            <Route path="utilisateurs" element={<AdminUtilisateurs />} />
            <Route path="enquetes"      element={<AdminEnquetes />} />
            <Route path="emails"        element={<AdminEmails />} />
          </Route>
          <Route path="/*" element={<AppLayout />} />
        </Routes>
        </Suspense>
      </AuthProvider>
      </SiteConfigProvider>
    </BrowserRouter>
  );
}
